import type { DddCardEvent, DddParsedFile } from '@repo/shared'

export const DDD_VIEWER_TAB_KEYS = [
  'overview',
  'activities',
  'vehicles',
  'places',
  'events',
  'faults',
  'conditions',
  'compliance',
  'controls',
  'companies',
  'downloads',
  'drivers',
  'speed',
  'borders',
  'cargo',
  'technical',
] as const

export type DddViewerTabKey = (typeof DDD_VIEWER_TAB_KEYS)[number]

/** Tabs whose data exists only in Gen2 v2 (smart tachograph v2) files. */
const GEN2_V2_ONLY: ReadonlySet<DddViewerTabKey> = new Set(['borders', 'cargo'])

export type DddViewerTab = {
  key: DddViewerTabKey
  /** Record count for the badge; null when the tab has no countable data. */
  count: number | null
}

type TabSource = Pick<
  DddParsedFile,
  | 'meta'
  | 'activities'
  | 'cardEvents'
  | 'places'
  | 'events'
  | 'faults'
  | 'specificConditions'
  | 'controlActivities'
  | 'vuDownloads'
  | 'companyLocks'
  | 'vuCardSessions'
>

/** Tabs that only make sense for vehicle unit files. */
const VU_ONLY: ReadonlySet<DddViewerTabKey> = new Set([
  'companies',
  'downloads',
  'drivers',
  'speed',
  'technical',
])

/** Tabs that only make sense for driver card files. */
const CARD_ONLY: ReadonlySet<DddViewerTabKey> = new Set(['controls'])

export function getDddViewerTabs(parsed: TabSource): Array<DddViewerTab> {
  const isGen2V2 = parsed.meta.generation === 'GEN2_V2'
  const isVu = parsed.meta.kind === 'vu'
  return DDD_VIEWER_TAB_KEYS.filter(
    (key) =>
      (isGen2V2 || !GEN2_V2_ONLY.has(key)) &&
      (isVu || !VU_ONLY.has(key)) &&
      (!isVu || !CARD_ONLY.has(key)),
  ).map((key) => ({ key, count: countFor(key, parsed) }))
}

function countFor(key: DddViewerTabKey, parsed: TabSource): number | null {
  switch (key) {
    case 'activities':
      return countActivityDays(parsed.activities)
    case 'vehicles':
      return parsed.meta.kind === 'card'
        ? groupCardVehicles(parsed.cardEvents).length
        : null
    case 'places':
      return parsed.places.length
    case 'events':
      return parsed.events.length
    case 'faults':
      return parsed.faults.length
    case 'conditions':
      return parsed.specificConditions.length
    case 'controls':
      return parsed.controlActivities.length
    case 'companies':
      return parsed.companyLocks.length
    case 'drivers':
      return parsed.vuCardSessions.length
    case 'downloads':
      return parsed.vuDownloads.length + parsed.controlActivities.length
    default:
      return null
  }
}

/** Distinct UTC calendar days that have at least one activity record. */
function countActivityDays(activities: TabSource['activities']): number {
  return new Set(activities.map((a) => a.startTime.slice(0, 10))).size
}

type SessionGroup = {
  key: string
  from: string
  /** Last withdrawal; null when a session is still open. */
  to: string | null
  sessions: number
  /** Sum of per-session odometer deltas; null when no session has both ends. */
  km: number | null
  first: DddCardEvent
}

/**
 * Groups insert/remove card events by `keyOf`, pairing them chronologically
 * into sessions. A group with fewer removals than insertions has an open
 * session → `to = null`. Sorted most recent first.
 */
function groupSessions(
  events: Array<DddCardEvent>,
  keyOf: (e: DddCardEvent) => string | null,
): Array<SessionGroup> {
  const groups = new Map<string, Array<{ ts: number; e: DddCardEvent }>>()
  for (const e of events) {
    const key = keyOf(e)
    const ts = Date.parse(e.eventTime)
    if (!key || Number.isNaN(ts)) continue
    const list = groups.get(key) ?? []
    list.push({ ts, e })
    groups.set(key, list)
  }

  return [...groups.entries()]
    .map(([key, list]) => {
      list.sort((a, b) => a.ts - b.ts)
      let from = Number.POSITIVE_INFINITY
      let to: number | null = null
      let inserted = 0
      let removed = 0
      let km: number | null = null
      let openOdo: number | null = null
      for (const { ts, e } of list) {
        if (e.eventType === 'inserted') {
          inserted += 1
          from = Math.min(from, ts)
          openOdo = e.odometerKm
        } else {
          removed += 1
          to = to === null ? ts : Math.max(to, ts)
          if (openOdo !== null && e.odometerKm !== null) {
            const delta = e.odometerKm - openOdo
            if (delta >= 0) km = (km ?? 0) + delta
          }
          openOdo = null
        }
      }
      if (!Number.isFinite(from)) from = list[0].ts
      const open = removed < inserted || to === null
      return {
        key,
        from: new Date(from).toISOString(),
        to: open ? null : new Date(to as number).toISOString(),
        sessions: inserted,
        km,
        first: list[0].e,
        sortKey: open ? Number.POSITIVE_INFINITY : (to as number),
      }
    })
    .sort((a, b) => b.sortKey - a.sortKey || b.from.localeCompare(a.from))
    .map(({ sortKey: _sortKey, ...row }) => row)
}

export type DddCardVehicle = {
  vrn: string
  /** Registration nation (tacho enum name); first non-null across the VRN's sessions. */
  nation: string | null
  from: string
  /** Last withdrawal; null when the card is still in this vehicle. */
  to: string | null
  sessions: number
  km: number | null
}

/** Card "vehicles used" collapsed into one row per VRN, most recent first. */
export function groupCardVehicles(
  events: Array<DddCardEvent>,
): Array<DddCardVehicle> {
  const nationByVrn = new Map<string, string>()
  for (const e of events) {
    if (e.vrn && e.vrnNation && !nationByVrn.has(e.vrn)) {
      nationByVrn.set(e.vrn, e.vrnNation)
    }
  }
  return groupSessions(events, (e) => e.vrn).map(
    ({ key, from, to, sessions, km }) => ({
      vrn: key,
      nation: nationByVrn.get(key) ?? null,
      from,
      to,
      sessions,
      km,
    }),
  )
}

export type DddVuDriver = {
  cardNumber: string | null
  holderName: string | null
  from: string
  to: string | null
  sessions: number
  km: number | null
}

/** VU card insertions collapsed into one row per driver card, most recent first. */
export function groupVuDrivers(
  events: Array<DddCardEvent>,
): Array<DddVuDriver> {
  return groupSessions(events, (e) => e.cardNumber ?? e.holderName).map(
    ({ first, from, to, sessions, km }) => ({
      cardNumber: first.cardNumber,
      holderName: first.holderName,
      from,
      to,
      sessions,
      km,
    }),
  )
}
